/**
 * Event-type capability helpers.
 *
 * Agapaé supports three event types (wire format from api/models.mjs):
 *   - "wedding"          — default; full wedding feature set
 *   - "baptism"          — wedding-only concepts hidden (Side, honor attendants)
 *   - "wedding_baptism"  — combined same-day event (common in Greece); keeps the
 *                          full wedding feature set, plus the baby is additive
 *
 * Centralize all "does this event type show feature X?" decisions here instead
 * of scattering `eventType === 'baptism'` checks across components. Wedding-only
 * capabilities are visible for `wedding` AND `wedding_baptism`, hidden only for
 * pure `baptism`. Every hidden feature is tracked in
 * .claude/rules/EVENT-TYPE-INCOMPATIBILITIES.md.
 */

export type EventType = 'wedding' | 'baptism' | 'wedding_baptism';

/** Default applied when an event predates the `eventType` field. */
export const DEFAULT_EVENT_TYPE: EventType = 'wedding';

export const isBaptism = (t?: EventType | null): boolean => t === 'baptism';

export const isWeddingBaptism = (t?: EventType | null): boolean => t === 'wedding_baptism';

/**
 * Whether the wedding "Side" (bride/groom) concept applies — guest Side filter,
 * column, and edit control. True for weddings and combined events; false only
 * for a standalone baptism.
 */
export const hasWeddingSides = (t?: EventType | null): boolean => t !== 'baptism';

/**
 * Whether wedding honor attendants (best men / maids of honor) apply. Same rule
 * as sides — hidden only for a standalone baptism.
 */
export const hasHonorAttendants = (t?: EventType | null): boolean => t !== 'baptism';
